///
/// EarthBrowser - Hurricane Browser
///
/// Movable window for browsing active tropical cyclones using local sync data
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { useCallback, useMemo, useState, useEffect, memo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { sendDatasetAction } from '@/redux/earthBrowserSlice'
import { DatasetBrowserWindow } from '@/components/windows/DatasetBrowserWindow'
import { DatasetVisibilityToggle } from '@/components/windows/DatasetVisibilityToggle'
import { DataBrowserView, type DataBrowserViewHandle } from '@/components/data-browser/DataBrowserView'
import { useRegisterWindowContent } from '@/components/windows/WindowContentRegistry'
import { DataProviderRegistry } from '@/data-providers/DataProviderRegistry'
import { HurricaneDataProvider } from '@/data-providers/HurricaneDataProvider'
import { HurricanesTable, type StormProperties } from '@/earthbrowser/dataset-distribution/hurricanes/HurricanesTable'
import { useDatasetLayerRefresh } from '@/hooks/useDatasetLayerRefresh'
import type { DataRow } from '@/data-providers/types'
import { Wind } from 'lucide-react'
import { isMobile } from '@/earthbrowser/utils/platform'
import { closeDatasetWindow, type DatasetBrowserWindowState } from '@/redux/dialogSlice'

interface HurricaneBrowserRendererProps {
	windowId: string
	windowState: DatasetBrowserWindowState
}

export const HurricaneBrowserRenderer = memo(function HurricaneBrowserRenderer({
	windowId,
	windowState,
}: HurricaneBrowserRendererProps) {
	const dispatch = useAppDispatch()
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)
	const hurricanesVisible = visibleDatasets.includes("hurricane")
	const cloudsVisible = visibleDatasets.includes("cloud")

	// Get singleton scene manager from Redux
	const layerManager = useAppSelector(state => state.datasetScene.layerManager)

	// Local state
	const [isInitialized, setIsInitialized] = useState(false)
	const [isLoading, setIsLoading] = useState(true)
	const [storms, setStorms] = useState<StormProperties[]>([])
	const [totalCount, setTotalCount] = useState(0)
	const [timeUpdateTrigger, setTimeUpdateTrigger] = useState(0)
	const dataBrowserViewRef = useRef<DataBrowserViewHandle>(null)

	// Load storms from the local table (mount, sync refresh, manual refresh)
	const loadStorms = useCallback(async (showLoading: boolean) => {
		if (showLoading) setIsLoading(true)
		try {
			const loadedStorms = await HurricanesTable.getAll()
			setStorms(loadedStorms)
			setTimeUpdateTrigger(prev => prev + 1)

			const count = await HurricanesTable.getCount()
			setTotalCount(count)
		} catch (error) {
			console.error('Failed to load hurricanes:', error)
		} finally {
			if (showLoading) setIsLoading(false)
		}
	}, [])

	// Load storms on mount
	useEffect(() => {
		void loadStorms(true)
	}, [loadStorms])

	// Reload silently whenever the hurricanes layer refreshes after a sync,
	// so the table doesn't show a stale snapshot from when the window opened
	useDatasetLayerRefresh('hurricanes', () => {
		void loadStorms(false)
	})

	// Initialize and update data provider
	useEffect(() => {
		if (isLoading) return

		if (!isInitialized) {
			const oldProvider = DataProviderRegistry.get(windowId)
			if (oldProvider) {
				void oldProvider.dispose?.()
				DataProviderRegistry.unregister(windowId)
			}

			const provider = new HurricaneDataProvider({
				id: windowId,
				name: 'Hurricanes',
				queryFn: async () => storms // Initial data
			})

			void provider.initialize().then(() => {
				DataProviderRegistry.register(windowId, provider)
				setIsInitialized(true)
			})
		} else {
			const provider = DataProviderRegistry.get(windowId) as HurricaneDataProvider | undefined
			if (provider) {
				provider.updateCachedData(storms)
			}
		}
	}, [storms, windowId, isLoading, isInitialized])

	// Timer to update relative times every minute (synced to minute boundary)
	useEffect(() => {
		let interval: NodeJS.Timeout | null = null

		const now = new Date()
		const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds()

		const timeout = setTimeout(() => {
			setTimeUpdateTrigger(prev => prev + 1)
			interval = setInterval(() => {
				setTimeUpdateTrigger(prev => prev + 1)
			}, 60000)
		}, msUntilNextMinute)

		return () => {
			clearTimeout(timeout)
			if (interval) {
				clearInterval(interval)
			}
		}
	}, [])

	// Sync scene layer visibility with Redux state
	useEffect(() => {
		if (layerManager) {
			layerManager.setLayerVisible('hurricanes', hurricanesVisible)
		}
	}, [layerManager, hurricanesVisible])

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			const provider = DataProviderRegistry.get(windowId)
			if (provider) {
				void provider.dispose?.()
				DataProviderRegistry.unregister(windowId)
			}
		}
	}, [windowId])

	// Handle storm row click: focus flies the camera to frame the storm
	const handleRowClick = useCallback((row: DataRow) => {
		const storm = row as unknown as StormProperties
		const featureId = storm.id

		// Ensure hurricanes are visible on the map
		if (!hurricanesVisible) {
			dispatch(sendDatasetAction({
				type: "hurricane",
				action: "enable"
			}))
			if (layerManager) {
				layerManager.setLayerVisible('hurricanes', true)
			}
		}

		// The storm's cloud structure is the IR cloud overlay (the marker is
		// only a dome and a symbol), so framing a storm brings the clouds
		// along. The clouds layer tracks its own redux visibility, and the
		// memory gate can still decline the enable on constrained devices
		if (!cloudsVisible) {
			dispatch(sendDatasetAction({
				type: "cloud",
				action: "enable"
			}))
		}

		if (layerManager) {
			layerManager.focusSceneObject('hurricanes', featureId)
		}

		if (isMobile()) {
			dispatch(closeDatasetWindow(windowId))
		}
	}, [layerManager, hurricanesVisible, cloudsVisible, dispatch, windowId])

	// Handle refresh
	const handleRefresh = useCallback(() => {
		void loadStorms(true)
		if (layerManager) {
			void layerManager.refreshLayer('hurricanes')
		}
	}, [loadStorms, layerManager])

	// Content
	const content = useMemo(() => {
		const provider = DataProviderRegistry.get(windowId)

		if (provider && isInitialized) {
			return (
				<DataBrowserView
					ref={dataBrowserViewRef}
					key={`hurricane-browser-${storms.length}-${timeUpdateTrigger}`}
					provider={provider}
					onRowClick={handleRowClick}
					onRefresh={handleRefresh}
					totalDatabaseCount={totalCount}
					searchPlaceholder="Search by storm name or basin..."
				/>
			)
		}

		return (
			<div className="flex flex-col items-center justify-center h-full bg-black/20 backdrop-blur-sm">
				<div className="flex flex-col items-center gap-4 p-8">
					<div className="relative w-16 h-16">
						<div className="absolute inset-0 border-4 border-sky-500/20 rounded-full" />
						<div className="absolute inset-0 border-4 border-transparent border-t-sky-500 rounded-full animate-spin" />
					</div>
					<p className="text-base font-medium text-white">
						Loading Hurricanes
					</p>
					<p className="text-sm text-white/60">
						Initializing...
					</p>
				</div>
			</div>
		)
	}, [windowId, isInitialized, handleRowClick, handleRefresh, totalCount, storms.length, timeUpdateTrigger])

	// Register content with the window content registry for drawer docking
	const contentRenderer = useCallback(() => content, [content])
	useRegisterWindowContent(windowId, contentRenderer)

	// Handle window being brought to front - focus search input
	const handleBringToFront = useCallback(() => {
		dataBrowserViewRef.current?.focusSearch()
	}, [])

	return (
		<DatasetBrowserWindow
			windowState={windowState}
			icon={<Wind className="w-5 h-5" />}
			iconColor="text-sky-400"
			titlebarPrefix={<DatasetVisibilityToggle windowType="hurricane" />}
			onBringToFront={handleBringToFront}
		>
			{content}
		</DatasetBrowserWindow>
	)
})
